import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput, toE164 } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Phone, Loader2, KeyRound, Camera, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { getPerms } from "@/lib/permissions";
import { ROLE_LABEL } from "@/lib/format";

export default function Settings() {
  const { user, company, refresh } = useAuth();
  const perms = getPerms(user?.role);
  const [phone, setPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const fileRef = useRef(null);

  // FASE 19 — profile photo: resize client-side to 256px and store as data URL
  const onPhotoPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSavingPhoto(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(f);
        img.onload = () => {
          const size = 256;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          const min = Math.min(img.width, img.height);
          ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        img.src = url;
      });
      await api.patch("/auth/me", { picture: dataUrl });
      toast.success("Foto de perfil atualizada");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar foto");
    } finally {
      setSavingPhoto(false);
    }
  };

  const removePhoto = async () => {
    setSavingPhoto(true);
    try {
      await api.patch("/auth/me", { picture: "" });
      toast.success("Foto removida");
      if (refresh) await refresh();
    } catch (err) {
      toast.error("Falha ao remover foto");
    } finally {
      setSavingPhoto(false);
    }
  };

  // Sync phone from user once loaded (not on every clear)
  const phoneSynced = useRef(false);
  useEffect(() => {
    if (user && !phoneSynced.current) {
      phoneSynced.current = true;
      setPhone(user.phone || "");
    }
  }, [user]);

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await api.patch("/auth/me", { phone });
      toast.success("Telefone atualizado");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSavingPhone(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Senha alterada com sucesso");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao alterar senha");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <Layout title="Configurações" subtitle="Conta e perfil">
      <div data-testid={TID.settings} className="max-w-3xl">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-stone-100 border border-stone-200">
            <TabsTrigger value="profile">Perfil</TabsTrigger>
            {perms.companyTab && <TabsTrigger value="company">Empresa</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile" className="mt-5 space-y-5">
            <Card className="p-6 rounded-xl border-stone-200 bg-white space-y-4">
              {/* FASE 19 — profile photo */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20 border-2 border-stone-200" data-testid="profile-avatar">
                  <AvatarImage src={user?.picture || ""} alt={user?.name} />
                  <AvatarFallback className="text-lg bg-emerald-700 text-white">
                    {(user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Label className="text-sm font-body">Foto de perfil</Label>
                  <p className="text-xs text-stone-500 font-body mt-0.5 mb-2">
                    Ajuda o gestor a identificar você visualmente na equipe.
                  </p>
                  <div className="flex gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onPhotoPicked}
                      data-testid="profile-photo-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      disabled={savingPhoto}
                      data-testid="profile-photo-upload"
                    >
                      {savingPhoto ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Camera className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {user?.picture ? "Trocar foto" : "Enviar foto"}
                    </Button>
                    {user?.picture && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={removePhoto}
                        disabled={savingPhoto}
                        className="text-rose-600 hover:text-rose-700"
                        data-testid="profile-photo-remove"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-stone-200">
                <Label className="text-sm font-body">Nome</Label>
                <Input value={user?.name || ""} readOnly className="mt-1 bg-stone-50" />
              </div>
              <div>
                <Label className="text-sm font-body">E-mail</Label>
                <Input value={user?.email || ""} readOnly className="mt-1 bg-stone-50" />
              </div>
              <div>
                <Label className="text-sm font-body">Perfil</Label>
                <Input value={ROLE_LABEL[user?.role] || user?.role || ""} readOnly className="mt-1 bg-stone-50" />
              </div>

              <div className="pt-3 border-t border-stone-200">
                <Label className="text-sm font-body flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-emerald-700" />
                  Telefone WhatsApp
                </Label>
                <p className="text-xs text-stone-500 font-body mt-0.5 mb-2">
                  Cadastre seu número para receber alertas críticos no WhatsApp
                  (desvios APPCC, ocorrências críticas, etc).
                </p>
                <div className="flex gap-2">
                  <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    className="flex-1"
                    data-testid="profile-phone"
                  />
                  <Button
                    onClick={savePhone}
                    disabled={savingPhone || toE164(phone) === toE164(user?.phone || "")}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white"
                    data-testid="profile-phone-save"
                  >
                    {savingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                  </Button>
                </div>
              </div>
            </Card>

            {/* FASE 17.5 — change password */}
            <Card className="p-6 rounded-xl border-stone-200 bg-white">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="h-4 w-4 text-emerald-700" />
                <h3 className="font-display text-lg font-bold tracking-tight">Alterar senha</h3>
              </div>
              <p className="text-xs text-stone-500 font-body mb-4">
                Esqueceu a senha atual? Saia da conta e use "Esqueci minha senha" na tela de login.
              </p>
              <form onSubmit={changePassword} className="space-y-3">
                <div>
                  <Label className="text-sm font-body">Senha atual *</Label>
                  <Input
                    type="password"
                    value={pw.current_password}
                    onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                    required
                    className="mt-1 bg-white"
                    data-testid="password-current"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-body">Nova senha *</Label>
                    <Input
                      type="password"
                      value={pw.new_password}
                      onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                      required
                      minLength={6}
                      className="mt-1 bg-white"
                      data-testid="password-new"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-body">Confirmar nova senha *</Label>
                    <Input
                      type="password"
                      value={pw.confirm}
                      onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                      required
                      minLength={6}
                      className="mt-1 bg-white"
                      data-testid="password-confirm"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={savingPw}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="password-submit"
                >
                  {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar senha"}
                </Button>
              </form>
            </Card>
          </TabsContent>

          {perms.companyTab && (
            <TabsContent value="company" className="mt-5">
              <Card className="p-6 rounded-xl border-stone-200 bg-white space-y-4">
                <div>
                  <Label className="text-sm font-body">Razão Social</Label>
                  <Input value={company?.legal_name || "—"} readOnly className="mt-1 bg-stone-50" />
                </div>
                <div>
                  <Label className="text-sm font-body">Nome Fantasia</Label>
                  <Input value={company?.trade_name || "—"} readOnly className="mt-1 bg-stone-50" />
                </div>
                <div>
                  <Label className="text-sm font-body">CNPJ</Label>
                  <Input value={company?.cnpj || "—"} readOnly className="mt-1 bg-stone-50" />
                </div>
                <div>
                  <Label className="text-sm font-body">Status</Label>
                  <Input value={company?.status || "—"} readOnly className="mt-1 bg-stone-50" />
                </div>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
